/**
 * 점주 콘솔 v2 레이아웃 — 디자인 핸드오프 기반.
 *
 * 데스크톱: 212px 사이드바 + 상주 StoreStatusHeader + 1180px 콘텐츠.
 * 모바일: 사이드바 대신 하단 탭바. 페이지 전체 가로 스크롤은 만들지 않고,
 * 표처럼 필요한 영역만 내부 스크롤을 사용한다.
 */
import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import CardMembershipRoundedIcon from '@mui/icons-material/CardMembershipRounded';

import ErrorBoundary from '../components/ErrorBoundary';
import { useSession } from './permissions/SessionContext';
import { buildNavigationSections, ROLE_LABELS } from './navigation';
import SessionSwitcher from './components/SessionSwitcher';
import StoreStatusHeader from './components/StoreStatusHeader';
import { colors, layout, radius } from './design-system/tokens';
import mogooLogo from './assets-mogoo-logo.png';

const BASE_PATH = '/owner-v2';

const DISPLAY_LABELS = {
  dashboard: '홈',
  payments: '결제 · 정산',
};

const MOBILE_KEYS = ['dashboard', 'members', 'payments', 'membershipPlans'];

const MOBILE_ICONS = {
  dashboard: HomeRoundedIcon,
  members: GroupRoundedIcon,
  payments: ReceiptLongRoundedIcon,
  membershipPlans: CardMembershipRoundedIcon,
};

const getItemPath = (item) => (item.path ? `${BASE_PATH}/${item.path}` : BASE_PATH);
const getItemLabel = (item) => DISPLAY_LABELS[item.key] || item.label;

const OwnerConsoleLayout = () => {
  const {
    session,
    storeOptions,
    selectedStoreId,
    selectStore,
    recentStoreIds,
    masterQaMode,
  } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(`(max-width:${layout.mobileBreakpoint - 1}px)`);
  const sections = buildNavigationSections(session);
  const navItems = sections.flatMap((section) => section.items);
  const canUseLegacyAdmin = ['master', 'manager'].includes(session.user.role);
  const isMasterQa = Boolean(masterQaMode);

  const isSelected = (item) => location.pathname === getItemPath(item);

  const goTo = (item) => {
    navigate(getItemPath(item));
  };

  const handleStoreChange = (storeId) => {
    selectStore(storeId);
    navigate(BASE_PATH);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminName');
    localStorage.removeItem('adminStoreId');
    localStorage.removeItem('adminStoreCode');
    navigate('/login');
  };

  const sidebar = (
    <Box
      component="nav"
      sx={{
        position: 'sticky',
        top: 0,
        height: '100vh',
        width: layout.sidebarWidth,
        flexShrink: 0,
        display: 'flex',
        [`@media (max-width:${layout.mobileBreakpoint - 1}px)`]: {
          display: 'none',
        },
        flexDirection: 'column',
        bgcolor: colors.surface,
        borderRight: `1px solid ${colors.border.muted}`,
      }}
    >
      <Box sx={{ px: 2, pt: 2.25, pb: 1.75 }}>
        <Box
          sx={{
            minHeight: 42,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            component="img"
            src={mogooLogo}
            alt=""
            sx={{ width: 36, height: 36, borderRadius: `${radius.button}px`, flexShrink: 0 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontSize: 15.5, lineHeight: 1.15, fontWeight: 800 }}
            >
              모구 파트너
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.35, color: colors.text.muted, lineHeight: 1.15 }}
            >
              점주 콘솔
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.25, pb: 1 }}>
        {sections.map((section, sectionIndex) => (
          <Box key={section.section} sx={{ mb: sectionIndex === 0 ? 1 : 2 }}>
            {section.section !== 'core' && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 1.25,
                  pt: 1,
                  pb: 0.5,
                  color: colors.text.muted,
                  fontWeight: 700,
                }}
              >
                {section.title}
              </Typography>
            )}
            <List dense disablePadding>
              {section.items.map((item) => {
                const selected = isSelected(item);
                return (
                  <ListItemButton
                    key={item.key}
                    selected={selected}
                    onClick={() => goTo(item)}
                    sx={{
                      minHeight: 36,
                      px: 1.5,
                      mb: 0.25,
                      borderRadius: `${radius.sm}px`,
                      alignItems: 'center',
                      '&.Mui-selected': {
                        bgcolor: colors.brand.primarySoft,
                        color: colors.brand.primary,
                        boxShadow: `inset 3px 0 0 ${colors.brand.primary}`,
                      },
                    }}
                  >
                    <ListItemText
                      primary={getItemLabel(item)}
                      slotProps={{
                        primary: {
                          sx: {
                            fontSize: 13.5,
                            fontWeight: selected ? 700 : 500,
                            color: selected ? colors.brand.primary : colors.text.primary,
                            overflowWrap: 'anywhere',
                          },
                        },
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 1.25, borderTop: `1px solid ${colors.border.soft}` }}>
        {process.env.NODE_ENV !== 'production' && (
          <Box sx={{ mb: 1.25 }}>
            <SessionSwitcher />
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            sx={{
              width: 28,
              height: 28,
              bgcolor: colors.brand.primarySoft,
              color: colors.brand.primary,
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {session.user.name?.slice(0, 1) || '모'}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
              {session.user.name}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.text.muted }}>
              {ROLE_LABELS[session.user.role] || session.user.role}
            </Typography>
          </Box>
          <Tooltip title="로그아웃" arrow>
            <IconButton size="small" onClick={handleLogout} aria-label="로그아웃">
              <LogoutRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {canUseLegacyAdmin && (
          <>
            <Divider sx={{ my: 1.25 }} />
            <ButtonBase
              onClick={() => navigate('/')}
              sx={{
                width: '100%',
                minHeight: 32,
                borderRadius: `${radius.sm}px`,
                color: colors.text.secondary,
                justifyContent: 'flex-start',
                gap: 1,
                px: 1,
                fontSize: 13,
                fontWeight: 700,
                textAlign: 'left',
                '&:hover': { bgcolor: colors.canvas.subtle },
              }}
            >
              <SwapHorizRoundedIcon fontSize="small" />
              관리자 콘솔
            </ButtonBase>
          </>
        )}
      </Box>
    </Box>
  );

  const bottomNav = (
    <Box
      component={isMobile ? 'nav' : 'div'}
      aria-label={isMobile ? '모바일 주요 메뉴' : undefined}
      sx={{
        display: 'none',
        [`@media (max-width:${layout.mobileBreakpoint - 1}px)`]: {
          display: 'grid',
        },
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 12,
        bgcolor: colors.surface,
        borderTop: `1px solid ${colors.border.muted}`,
        pb: 'env(safe-area-inset-bottom)',
      }}
    >
      {navItems
        .filter((item) => MOBILE_KEYS.includes(item.key))
        .slice(0, 4)
        .map((item) => {
          const selected = isSelected(item);
          const Icon = MOBILE_ICONS[item.key] || HomeRoundedIcon;
          return (
            <ButtonBase
              key={item.key}
              onClick={() => goTo(item)}
              sx={{
                minHeight: 58,
                minWidth: 0,
                px: 0.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                color: selected ? colors.brand.primary : colors.text.muted,
                fontSize: 11.5,
                fontWeight: selected ? 800 : 600,
                lineHeight: 1.15,
                textAlign: 'center',
                overflowWrap: 'anywhere',
              }}
            >
              <Icon fontSize="small" />
              {getItemLabel(item)}
            </ButtonBase>
          );
        })}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: colors.background,
        color: colors.text.primary,
        wordBreak: 'keep-all',
        overflowWrap: 'anywhere',
        minWidth: 0,
      }}
    >
      {sidebar}
      <Box
        sx={{
          minWidth: 0,
          flex: 1,
          pb: 0,
          [`@media (max-width:${layout.mobileBreakpoint - 1}px)`]: {
            pb: 11,
          },
        }}
      >
        <StoreStatusHeader
          session={session}
          storeOptions={storeOptions}
          selectedStoreId={selectedStoreId}
          recentStoreIds={recentStoreIds}
          masterQaMode={isMasterQa}
          onStoreChange={handleStoreChange}
        />
        {process.env.NODE_ENV !== 'production' && isMobile && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            <SessionSwitcher />
          </Box>
        )}
        <Box
          sx={{
            maxWidth: layout.contentMaxWidth,
            mx: 'auto',
            px: { xs: 2, sm: 2.5, lg: layout.pagePaddingX },
            py: { xs: 2.25, sm: 2.75, lg: layout.pagePaddingY },
            boxSizing: 'border-box',
            minWidth: 0,
          }}
        >
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </Box>
      </Box>
      {bottomNav}
    </Box>
  );
};

export default OwnerConsoleLayout;
